import { TELEMETRY_SEVERITY, } from '@proctoring/shared';
const PERMISSION_LABELS = {
    screen_recording: 'screen recording',
    camera: 'camera',
    microphone: 'microphone',
};
/** Formats a millisecond duration compactly: "800 ms", "4.2 s", "3 m 05 s". */
export function formatDuration(ms) {
    if (ms < 1000)
        return `${Math.round(ms)} ms`;
    const seconds = ms / 1000;
    if (seconds < 60)
        return `${seconds.toFixed(1)} s`;
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return `${minutes} m ${String(rest).padStart(2, '0')} s`;
}
/** Page titles and typed queries are untrusted text; keep them short. */
function clip(text, limit = 80) {
    const trimmed = text.trim();
    return trimmed.length > limit ? `${trimmed.slice(0, limit - 1)}…` : trimmed;
}
/** Window titles are attacker-controlled text; keep them short and clearly quoted. */
function windowLabel(processName, windowTitle) {
    const trimmed = windowTitle.trim();
    if (trimmed.length === 0)
        return processName;
    return `${processName} — “${clip(trimmed)}”`;
}
/**
 * How a navigation is introduced in the feed.
 *
 * The distinction matters because §3's policy is open: a candidate typing an address is
 * doing something allowed, and an interviewer's push is not the candidate's doing at all.
 * Neutral wording throughout — this feed is a record, not an allegation.
 */
const NAVIGATION_PREFIX = {
    address_bar: 'The candidate went to',
    search: 'The candidate searched —',
    link: 'The candidate followed a link to',
    interviewer: 'An interviewer pushed',
};
/**
 * A URL, whole but bounded.
 *
 * The full string is kept rather than reduced to a host: the query and path are frequently
 * the point (a search term, a specific doc page), and truncating to a domain would throw
 * away the part of the evidence an interviewer is actually reading.
 */
function urlLabel(url) {
    const trimmed = url.trim();
    if (trimmed.length === 0)
        return 'a blank tab';
    return clip(trimmed, 120);
}
/**
 * Turns a watchdog event into interviewer-facing prose.
 *
 * The switch is exhaustive over `TelemetryEvent['type']`: the `never` assignment in the
 * default branch makes adding an event type to the shared schema a compile error here,
 * which is the point — a new detection must never reach the feed as an unlabelled row.
 */
export function describeEvent(event) {
    const severity = TELEMETRY_SEVERITY[event.type];
    switch (event.type) {
        case 'focus_lost':
            return {
                title: 'Focus lost',
                detail: `The interview window lost focus to ${windowLabel(event.processName, event.windowTitle)}.`,
                severity,
            };
        case 'focus_regained':
            return {
                title: 'Focus regained',
                detail: `The interview window regained focus after ${formatDuration(event.awayMs)} away.`,
                severity,
            };
        case 'overlay_window_detected':
            return {
                title: 'Overlay window',
                detail: event.captureTransparent
                    ? `A capture-transparent overlay is present but absent from the screen share: ${windowLabel(event.processName, event.windowTitle)}.`
                    : `An unexpected window is layered over the interview: ${windowLabel(event.processName, event.windowTitle)}.`,
                severity,
            };
        case 'process_launched':
            return {
                title: 'Process launched',
                detail: `A new process started: ${windowLabel(event.processName, event.windowTitle)} (pid ${event.pid}).`,
                severity,
            };
        case 'display_connected':
            return {
                title: 'Display connected',
                detail: `An additional display was connected — ${event.displayCount} now attached.`,
                severity,
            };
        case 'display_disconnected':
            return {
                title: 'Display disconnected',
                detail: event.displayCount === 1
                    ? 'A display was disconnected — back to a single display.'
                    : `A display was disconnected — ${event.displayCount} now attached.`,
                severity,
            };
        case 'vm_or_remote_desktop_detected':
            return {
                title: 'VM or remote desktop',
                detail: `The machine looks virtualised or remotely controlled (indicator: ${event.indicator}).`,
                severity,
            };
        case 'session_terminated_by_candidate':
            return {
                title: 'Session terminated',
                detail: 'The candidate closed the app, which ends the session.',
                severity,
            };
        case 'session_disconnected_unexpectedly':
            /*
             * Wording chosen to describe the connection, not the candidate. This row is produced
             * by an unload handler that genuinely cannot distinguish a crash, a reload and a
             * pulled power cable — so it states the one thing that is actually known and leaves
             * the inference to the reader, rather than asserting intent the code never observed.
             */
            return {
                title: 'Connection lost',
                detail: "The candidate's app disconnected unexpectedly, without leaving the session.",
                severity,
            };
        case 'permission_revoked_midsession':
            return {
                title: 'Permission revoked',
                detail: `The candidate revoked ${PERMISSION_LABELS[event.permission]} permission mid-session.`,
                severity,
            };
        case 'network_dropped':
            return {
                title: 'Network dropped',
                detail: "The candidate's connection to the session was lost.",
                severity,
            };
        case 'network_rejoined':
            return {
                title: 'Network rejoined',
                detail: `The candidate reconnected after ${formatDuration(event.downtimeMs)} offline.`,
                severity,
            };
        case 'browser_navigated':
            return {
                title: 'Page visited',
                detail: event.source === 'search' && event.query !== undefined
                    ? `The candidate searched for “${clip(event.query)}” — ${urlLabel(event.url)}.`
                    : `${NAVIGATION_PREFIX[event.source]} ${urlLabel(event.url)}${event.title.trim().length > 0 ? ` (${clip(event.title)})` : ''}.`,
                severity,
            };
        case 'browser_tab_opened':
            return {
                title: 'Tab opened',
                detail: event.openedBy === 'page'
                    ? `A page opened a new tab — ${event.tabCount} now open.`
                    : `The candidate opened a new tab — ${event.tabCount} now open.`,
                severity,
            };
        case 'browser_tab_closed':
            return {
                title: 'Tab closed',
                detail: `The candidate closed ${urlLabel(event.url)} — ${event.tabCount} still open.`,
                severity,
            };
        case 'browser_tab_switched':
            return {
                title: 'Tab switched',
                detail: `The candidate switched to ${urlLabel(event.url)}.`,
                severity,
            };
        case 'interviewer_message_sent':
            return {
                title: 'Message sent',
                /*
                 * The body is quoted in full rather than clipped like a window title: this row is
                 * the record of what the candidate was actually given, and a truncated temporary
                 * password or snippet would make §6's export unable to answer the one question it
                 * is read for. The schema's 2000-char ceiling is what bounds it.
                 */
                detail: `${event.sentByName} sent the candidate: “${event.body.trim()}”`,
                severity,
            };
        case 'screen_share_started':
            return {
                title: 'Screen share started',
                detail: 'The candidate started sharing their screen.',
                severity,
            };
        case 'screen_share_stopped':
            return {
                title: 'Screen share stopped',
                /*
                 * Two sentences, neither of them an allegation. Stopping is allowed, so the wording
                 * states the consequence — the screen is no longer visible — rather than implying
                 * intent. `track_ended` is called out separately because it is frequently *not* the
                 * candidate's doing at all, and reading it as a deliberate stop would be unfair.
                 */
                detail: event.reason === 'track_ended'
                    ? 'The screen capture ended on the candidate’s machine — their screen is no longer visible.'
                    : 'The candidate stopped sharing their screen — it is no longer visible.',
                severity,
            };
        default: {
            const exhaustive = event;
            return exhaustive;
        }
    }
}
//# sourceMappingURL=describe-event.js.map